// src/features/blog/utils/blogHelpers.js
import {
  BLOG_STATUS,
  BLOG_STATUS_LABELS,
  BLOG_STATUS_COLORS,
  BLOG_STATUS_ICONS,
  BLOG_CATEGORIES,
} from "../types/blogTypes";

/**
 * Generate slug from title
 * @param {string} title - Blog post title
 * @returns {string} Generated slug
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

/**
 * Get status label
 */
export const getStatusLabel = (status) => {
  return BLOG_STATUS_LABELS[status] || status;
};

/**
 * Get status color
 */
export const getStatusColor = (status) => {
  return BLOG_STATUS_COLORS[status] || "#d9d9d9";
};

/**
 * Get status icon
 */
export const getStatusIcon = (status) => {
  return BLOG_STATUS_ICONS[status] || "📄";
};

/**
 * Get category label
 */
export const getCategoryLabel = (category) => {
  const found = BLOG_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

/**
 * Truncate text
 */
export const truncateText = (text, length = 150) => {
  if (!text) return "";
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
};

/**
 * Format date
 */
export const formatDate = (date, format = "MMM DD, YYYY") => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Calculate reading time
 */
export const getReadingTime = (content) => {
  if (!content) return 0;
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Extract image URLs from content
 */
export const extractImagesFromContent = (content) => {
  const regex = /<img[^>]+src="([^">]+)"/g;
  const matches = [...content.matchAll(regex)];
  return matches.map((m) => m[1]);
};

/**
 * Clean HTML content for preview
 */
export const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

/**
 * Filter blog posts
 */
export const filterBlogPosts = (posts, filters = {}) => {
  let result = [...posts];

  if (filters.status && filters.status !== "all") {
    result = result.filter((p) => p.status === filters.status);
  }

  if (filters.category && filters.category !== "all") {
    result = result.filter((p) => p.category === filters.category);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    result = result.filter(
      (p) =>
        p.title.toLowerCase().includes(searchLower) ||
        p.excerpt?.toLowerCase().includes(searchLower) ||
        p.content?.toLowerCase().includes(searchLower),
    );
  }

  if (filters.tags && filters.tags.length > 0) {
    result = result.filter(
      (p) => p.tags && filters.tags.some((tag) => p.tags.includes(tag)),
    );
  }

  return result;
};

/**
 * Sort blog posts
 */
export const sortBlogPosts = (
  posts,
  sortBy = "createdAt",
  sortOrder = "desc",
) => {
  const sorted = [...posts];

  sorted.sort((a, b) => {
    let valueA = a[sortBy];
    let valueB = b[sortBy];

    if (
      sortBy === "createdAt" ||
      sortBy === "updatedAt" ||
      sortBy === "publishedAt"
    ) {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    } else if (sortBy === "viewCount" || sortBy === "likeCount") {
      valueA = parseInt(valueA) || 0;
      valueB = parseInt(valueB) || 0;
    } else if (sortBy === "title") {
      valueA = (valueA || "").toLowerCase();
      valueB = (valueB || "").toLowerCase();
    }

    if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
    if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};

/**
 * Validate blog post
 */
export const validateBlogPost = (data) => {
  const errors = {};

  if (!data.title || data.title.length < 3) {
    errors.title = "Title must be at least 3 characters";
  }

  if (!data.content || data.content.length < 50) {
    errors.content = "Content must be at least 50 characters";
  }

  if (!data.excerpt || data.excerpt.length < 10) {
    errors.excerpt = "Excerpt must be at least 10 characters";
  }

  if (!data.category) {
    errors.category = "Category is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
